import { Button, Spin } from "antd";
import Back from "components/Misc/Back";
import useFallcackCalc from "components/hooks/useFallcackCalc";
import useLazyLoadForceReadPdf from "components/hooks/useLazyLoadForceReadPdf";
import LazyLoadPdfViewer from "components/PDF/LazyLoadPdfViewer";
import MoneyConnectHeader from "containers/App/MoneyConnectHeader";
import { HeaderHeight, MncVars, NavigatorHeight } from "const";
import IntlMessages from "components/Message/IntlMessages";
import Portal from "components/Portal";
import { MenuSelector } from "redux/selector/menu-selector";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { DocumentUtil } from "utils/common-util";
import { execNumber, getPropertyValue } from "utils/misc/css-util";
import styled from "styled-components";
import classNames from "classnames";

function LazyLoadForceReadPdf({
  pdfUrl,
  pdfFile,
  downloadInfo,
  title,
  visible,
  goBack,
  onAccept,
  accepting = false,
  hasAccepted = false,
  adjustPdfViewSize = -83,
  scale = 1,
  portalClassName,
  containerStyle = containerStyled,
  pdfStageStyle = pdfStageInlineStyle,
  acceptLabel,
  renderAcceptButton,
}) {
  const forceHideAllMenu = useSelector(MenuSelector.forceHideAllMenu);
  const pdfStageRef = useRef(null);
  const hasPdfSource = !!pdfUrl || !!pdfFile;

  const {
    pdf,
    reachEnd,
    pdfLoaded,
    initialWindowReady,
    isViewerSpinning,
    downloadable,
    onDocumentLoaded,
    onInitialPagesLoaded,
    onReachEnd,
    onDownloadPDF,
    scrollEndOptions,
  } = useLazyLoadForceReadPdf({
    pdfUrl,
    pdfFile,
    downloadInfo,
    enabled: visible && hasPdfSource,
  });

  const readChromeHeights = useCallback(
    () => ({
      header: execNumber(getPropertyValue({ varName: MncVars.Header, fallback: HeaderHeight })) || HeaderHeight,
      navbar: execNumber(getPropertyValue({ varName: MncVars.Navbar, fallback: NavigatorHeight })) || NavigatorHeight,
    }),
    [],
  );

  const [chromeHeights, setChromeHeights] = useState(readChromeHeights);

  useLayoutEffect(() => {
    if (!visible) return undefined;

    const syncChromeHeights = () => {
      const next = readChromeHeights();
      setChromeHeights((prev) =>
        prev.header === next.header && prev.navbar === next.navbar ? prev : next,
      );
    };

    syncChromeHeights();
    const raf = requestAnimationFrame(syncChromeHeights);
    const timers = [50, 100, 150, 300].map((ms) => setTimeout(syncChromeHeights, ms));

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [visible, readChromeHeights, pdf, pdfLoaded, initialWindowReady]);

  const headerHeight = chromeHeights.header;
  const navbarHeight = chromeHeights.navbar;
  const adjust = -((forceHideAllMenu ? 0 : navbarHeight) + headerHeight) + (adjustPdfViewSize ?? 0);

  useFallcackCalc(pdfStageRef, "--force-pdf-container-height", 100, {
    adjust,
  });

  useEffect(() => {
    if (visible) {
      DocumentUtil.disableScroll();
    } else {
      DocumentUtil.enableScroll();
    }
    return () => DocumentUtil.enableScroll();
  }, [visible]);

  const renderBottom = useCallback(() => {
    if (!reachEnd || !pdfLoaded || hasAccepted) return null;

    const acceptButtonProps = {
      onAccept,
      accepting,
      disabled: accepting || !reachEnd,
      reachEnd,
      hasAccepted,
    };

    return (
      <AcceptBottomBar>
        {renderAcceptButton ? (
          renderAcceptButton(acceptButtonProps)
        ) : (
          <Button
            className="btn button-ktb-main w-100 mx-3"
            onClick={onAccept}
            disabled={acceptButtonProps.disabled}
            loading={accepting}
          >
            {acceptLabel ?? <IntlMessages id="global.acknowledge" />}
          </Button>
        )}
      </AcceptBottomBar>
    );
  }, [acceptLabel, accepting, hasAccepted, onAccept, pdfLoaded, reachEnd, renderAcceptButton]);

  if (!visible || !hasPdfSource) return null;

  return (
    <Portal
      portalClass={classNames("portal position-fixed top-0 start-0 w-100", portalClassName)}
      styles={containerStyle}
    >
      <MoneyConnectHeader />
      <ActionBar
        title={title}
        downloadable={downloadable}
        onDownloadPDF={onDownloadPDF}
        goBack={goBack}
        accepting={accepting}
      />

      <Spin spinning={isViewerSpinning}>
        <PdfStage ref={pdfStageRef} style={pdfStageStyle} $adjust={adjust}>
          {pdf ? (
            <LazyLoadPdfViewer
              file={pdf}
              scale={scale}
              onDocumentLoaded={onDocumentLoaded}
              onInitialPagesLoaded={onInitialPagesLoaded}
              onReachEnd={onReachEnd}
              renderBottom={renderBottom}
              scrollEndOptions={scrollEndOptions}
            />
          ) : null}
        </PdfStage>
      </Spin>
    </Portal>
  );
}

export default LazyLoadForceReadPdf;

const containerStyled = { zIndex: 999, background: "#f0f2f5" };
const pdfStageInlineStyle = { width: "100vw", zIndex: 999 };

const PdfStage = styled.div`
  overflow: hidden;
  border-radius: 5px;

  height: var(--force-pdf-container-height);
  height: calc(var(--app-height) + ${(props) => props.$adjust ?? 0}px);
  height: calc(100vh + ${(props) => props.$adjust ?? 0}px);
  height: calc(100dvh + ${(props) => props.$adjust ?? 0}px);
`;

const AcceptBottomBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 8px 0 max(32px, env(safe-area-inset-bottom, 32px));
`;

const ActionBar = ({ title, goBack, downloadable, onDownloadPDF, accepting }) => (
  <div>
    <div className="d-flex align-items-center justify-content-between py-2">
      <Back goBack={goBack} disabled={accepting} />
      {downloadable && (
        <i
          className="feather-icon icon-download cursor-pointer me-3"
          style={{ fontSize: 22 }}
          title="download"
          onClick={onDownloadPDF}
        />
      )}
    </div>
    {title !== null && (
      <h5 className="ps-3 py-2 m-0 color-primary-navy-blue">
        {title ?? <IntlMessages id="perp-bond.risk-document" />}
      </h5>
    )}
  </div>
);
